// GST Rule Engine for Purchase Orders
export type GSTTreatment = 'cgst_sgst' | 'igst' | 'cgst_utgst';

export interface GSTBreakdown {
  cgst?: number;
  sgst?: number;
  igst?: number;
  utgst?: number;
}

// Indian State Codes for GST
export const INDIAN_STATES = {
  '10': 'Jammu and Kashmir',
  '09': 'Himachal Pradesh', 
  '21': 'Punjab',
  '31': 'Chandigarh',
  '28': 'Uttarakhand',
  '08': 'Haryana',
  '34': 'Delhi',
  '22': 'Rajasthan',
  '27': 'Uttar Pradesh',
  '04': 'Bihar',
  '23': 'Sikkim',
  '02': 'Arunachal Pradesh',
  '19': 'Nagaland',
  '16': 'Manipur',
  '18': 'Mizoram',
  '26': 'Tripura',
  '17': 'Meghalaya',
  '03': 'Assam',
  '29': 'West Bengal',
  '11': 'Jharkhand',
  '20': 'Odisha',
  '05': 'Chhattisgarh',
  '14': 'Madhya Pradesh',
  '07': 'Gujarat',
  '32': 'Dadra and Nagar Haveli and Daman and Diu',
  '15': 'Maharashtra',
  '01': 'Andhra Pradesh',
  '12': 'Karnataka',
  '06': 'Goa',
  '33': 'Lakshadweep',
  '13': 'Kerala',
  '24': 'Tamil Nadu',
  '35': 'Puducherry',
  '30': 'Andaman and Nicobar Islands',
  '25': 'Telangana',
};


export const STATE_NAME_MAP: Record<string, string> = {
  "01": "Jammu & Kashmir", "02": "Himachal Pradesh", "03": "Punjab",
  "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana", "07": "Delhi",
  "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim",
  "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam",
  "19": "West Bengal", "20": "Jharkhand", "21": "Odisha",
  "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat",
  "25": "Daman & Diu", "26": "Dadra & Nagar Haveli", "27": "Maharashtra",
  "28": "Andhra Pradesh", "29": "Karnataka", "30": "Goa",
  "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu",
  "34": "Puducherry", "35": "Andaman & Nicobar", "36": "Telangana",
  "37": "Andhra Pradesh (new)", "38": "Ladakh",
};

// Union Territories that use CGST + UTGST
export const UNION_TERRITORIES = ["04", "25", "26", "31", "34", "35", "38"];

// Company's registered state (can be made configurable)
export const COMPANY_STATE_CODE = '29'; // Karnataka (Karnataka)

/**
 * Determines GST treatment based on supplier and buyer location
 */
export function determineGSTTreatment(
  supplierStateCode: string, 
  buyerStateCode: string = COMPANY_STATE_CODE
): GSTTreatment {
  // Same state - CGST + SGST
  if (supplierStateCode === buyerStateCode) {
    return 'cgst_sgst';
  }
  
  // Different states but one is Union Territory - CGST + UTGST
  if (UNION_TERRITORIES.includes(supplierStateCode) || UNION_TERRITORIES.includes(buyerStateCode)) {
    return 'cgst_utgst';
  }
  
  // Different states - IGST
  return 'igst';
}

/**
 * Converts total tax rate to GST breakdown based on treatment
 */
export function calculateGSTBreakdown(
  totalTaxRate: number, 
  treatment: GSTTreatment
): Required<GSTBreakdown> {
  const rate = Number(totalTaxRate) || 0;
  switch (treatment) {
    case 'cgst_sgst':
      return { cgst: rate / 2, sgst: rate / 2, igst: 0, utgst: 0 };
    case 'cgst_utgst':
      return { cgst: rate / 2, sgst: 0, igst: 0, utgst: rate / 2 };
    case 'igst':
      return { cgst: 0, sgst: 0, igst: rate, utgst: 0 };
    default:
      return { cgst: rate / 2, sgst: rate / 2, igst: 0, utgst: 0 };
  }
}

/**
 * Gets display label for GST treatment
 */
export function getGSTTreatmentLabel(treatment: GSTTreatment): string {
  switch (treatment) {
    case 'cgst_sgst':
      return 'CGST + SGST (Same State)';
    case 'igst':
      return 'IGST (Different State)';
    case 'cgst_utgst':
      return 'CGST + UTGST (Union Territory)';
    default:
      return 'CGST + SGST';
  }
}

/**
 * Extracts state code from GSTIN
 */
export function extractStateFromGSTIN(gstin: string): string | null {
  if (!gstin || gstin.length < 2) return null;
  return gstin.substring(0, 2);
}

/** Indian state list item (from API) */
export interface IndianStateItem {
  id: number | string;
  state_name: string;
  isUnionTerritories?: number;
}

/**
 * Our company state: Karnataka, id 12 in indianStates list.
 * Same state → CGST+SGST; different state + supplier isUnionTerritories 1 → CGST+UTGST; else IGST.
 * Uses state IDs directly from indianStates (not GSTIN codes).
 */
export function determineGSTTreatmentFromStateId(
  supplierStateId: number | string,
  indianStates: IndianStateItem[],
  ourStateId: number | string = 12 // Karnataka
): GSTTreatment {
  if (!indianStates?.length) {
    return 'cgst_sgst'; // Default fallback
  }
  const ourState = indianStates.find((s) => String(s.id) === String(ourStateId));
  const supplierState = indianStates.find((s) => String(s.id) === String(supplierStateId));
  
  if (!supplierState) {
    return 'cgst_sgst'; // Default fallback if supplier state not found
  }
  
  // Same state → CGST+SGST
  if (ourState && String(supplierState.id) === String(ourState.id)) {
    return 'cgst_sgst';
  }
  
  // Different state + supplier isUnionTerritories 1 → CGST+UTGST
  if (Number(supplierState.isUnionTerritories) === 1) {
    return 'cgst_utgst';
  }
  
  // Different state → IGST
  return 'igst';
}

/**
 * Applies GST rule engine to asset line
 */
export function applyGSTRules(
  assetLine: any,
  supplierGSTIN: string,
  overrideTreatment?: GSTTreatment
): any {
  const supplierStateCode = extractStateFromGSTIN(supplierGSTIN);
  
  let treatment: GSTTreatment;
  if (overrideTreatment) {
    treatment = overrideTreatment;
  } else if (supplierStateCode) {
    treatment = determineGSTTreatment(supplierStateCode);
  } else {
    treatment = 'cgst_sgst'; // Default fallback
  }
  
  // Prefer a single total rate — never sum overlapping IGST + CGST/SGST leftovers
  const igst = Number(assetLine.igst) || 0;
  const cgst = Number(assetLine.cgst) || 0;
  const sgst = Number(assetLine.sgst) || 0;
  const utgst = Number(assetLine.utgst) || 0;
  const totalTaxRate =
    Number(assetLine.taxRate) ||
    (igst > 0 ? igst : cgst + sgst + utgst);

  const breakdown = calculateGSTBreakdown(totalTaxRate, treatment);

  return {
    ...assetLine,
    ...breakdown,
    taxRate: totalTaxRate,
    gstTreatment: treatment,
  };
}

export type GSTType = 'IGST' | 'CGST_SGST' | 'CGST_UTGST';

export interface GSTSplitResult {
  gstType: GSTType;
  igst: number;
  cgst: number;
  sgst: number;
  utgst: number;
  placeOfSupply: string;
  placeOfSupplyName: string;
}

export function getStateCodeFromGSTIN(gstin: string): string {
  return gstin?.substring(0, 2) || "";
}

export function determineGSTSplit(
  customerGstin: string,
  gstTotal: number
): GSTSplitResult {
  const customerStateCode = getStateCodeFromGSTIN(customerGstin);
  const placeOfSupply = customerStateCode || COMPANY_STATE_CODE;
  const placeOfSupplyName = STATE_NAME_MAP[placeOfSupply] || "Unknown";

  // If no GSTIN or same state → Intra-state (CGST + SGST)
  if (!customerStateCode || customerStateCode === COMPANY_STATE_CODE) {
    return {
      gstType: 'CGST_SGST',
      igst: 0,
      cgst: gstTotal / 2,
      sgst: gstTotal / 2,
      utgst: 0,
      placeOfSupply,
      placeOfSupplyName,
    };
  }

  // Union Territory → CGST + UTGST
  // if (UNION_TERRITORIES.includes(customerStateCode)) {
  //   return {
  //     gstType: 'CGST_UTGST',
  //     igst: 0,
  //     cgst: Math.round(gstTotal / 2),
  //     sgst: 0,
  //     utgst: Math.round(gstTotal / 2),
  //     placeOfSupply,
  //     placeOfSupplyName,
  //   };
  // }

  // Inter-state → IGST
  return {
    gstType: 'IGST',
    igst: gstTotal,
    cgst: 0,
    sgst: 0,
    utgst: 0,
    placeOfSupply,
    placeOfSupplyName,
  };
}